/**
 * Project Manager Profile Page
 * Comprehensive profile management for project managers
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Avatar,
    Button,
    Tabs,
    Tab,
    TextField,
    Chip,
    Divider,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Alert,
    LinearProgress,
    Rating,
    Badge,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import PhoneField from '../components/common/PhoneField';
import {
    Person,
    Edit,
    Save,
    Cancel,
    Email,
    Phone,
    LocationOn,
    CalendarToday,
    Star,
    Verified,
    WorkOutline,
    School,
    Description,
    Add,
    Delete,
    Download,
    Upload,
    CheckCircle,
    Assignment,
    TrendingUp,
    People,
    AttachMoney,
    PhotoCamera,
    Settings,
    Notifications,
    Security,
    Language,
    Schedule,
    Business,
    Engineering,
    MyLocation
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import DialogCloseButton from '../components/common/DialogCloseButton';

// Tab Panel Component
const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
);

const ProjectManagerProfile = () => {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    // Profile data state
    const [profile, setProfile] = useState({
        // Personal Info
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: '+263 77 123 4567',
        location: 'Harare, Zimbabwe',
        bio: 'Experienced construction project manager with over 10 years in residential and commercial developments. Specialized in managing diaspora client projects with a focus on transparency and regular communication.',
        
        // Professional Info
        title: 'Senior Project Manager',
        company: 'Eqaya Professional Services',
        yearsExperience: 12,
        projectsManaged: 45,
        certifications: ['PMP', 'PRINCE2 Practitioner', 'Construction Management Certificate'],
        specializations: ['Residential Construction', 'Commercial Buildings', 'Renovation & Remodeling'],
        preferredLocations: ['Harare', 'Bulawayo', 'Mutare'],
        
        // Preferences
        availableForRemote: true,
        minProjectValue: 50000,
        maxProjectValue: 500000,
        preferredContactMethod: 'whatsapp',
        communicationFrequency: 'weekly',
        
        // Notifications
        emailNotifications: true,
        smsNotifications: false,
        whatsappNotifications: true,
        
        // Stats
        stats: {
            activeProjects: 8,
            completedProjects: 37,
            totalBudgetManaged: 4500000,
            clientSatisfaction: 4.8,
            onTimeDelivery: 92,
            avgProjectDuration: 8.5
        }
    });

    // Reviews data
    const [reviews, setReviews] = useState([
        {
            id: 1,
            client: 'John Moyo',
            project: 'Borrowdale Heights Residence',
            rating: 5,
            comment: 'Excellent project manager! Kept me informed every step of the way. Highly recommend.',
            date: '2024-11-15'
        },
        {
            id: 2,
            client: 'Sarah Mutasa',
            project: 'Glen Lorne Commercial Complex',
            rating: 4,
            comment: 'Very professional and organized. Good communication despite the time difference.',
            date: '2024-10-28'
        },
        {
            id: 3,
            client: 'Peter Chigumba',
            project: 'Mount Pleasant Family Home',
            rating: 5,
            comment: 'Outstanding service! The weekly reports were incredibly detailed.',
            date: '2024-09-20'
        }
    ]);

    // Portfolio data
    const [portfolio, setPortfolio] = useState([
        {
            id: 1,
            name: '5-Bedroom Mansion - Borrowdale',
            type: 'Residential',
            value: 350000,
            duration: '14 months',
            year: 2024,
            status: 'completed',
            image: null
        },
        {
            id: 2,
            name: 'Commercial Office Block - CBD',
            type: 'Commercial',
            value: 850000,
            duration: '18 months',
            year: 2023,
            status: 'completed',
            image: null
        },
        {
            id: 3,
            name: 'Apartment Complex - Avondale',
            type: 'Multi-family',
            value: 1200000,
            duration: '24 months',
            year: 2023,
            status: 'completed',
            image: null
        }
    ]);

    const handleSaveProfile = async () => {
        setLoading(true);
        setError('');
        try {
            // API call to save profile
            await new Promise(resolve => setTimeout(resolve, 1000));
            setSuccess('Profile updated successfully!');
            setEditMode(false);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Overview Tab
    const OverviewTab = () => (
        <Box>
            {/* Profile Header Card */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                    <IconButton 
                                        size="small" 
                                        sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                                    >
                                        <PhotoCamera fontSize="small" />
                                    </IconButton>
                                }
                            >
                                <Avatar 
                                    sx={{ width: 120, height: 120, fontSize: 48, bgcolor: 'primary.main' }}
                                >
                                    {profile.firstName?.[0]}{profile.lastName?.[0]}
                                </Avatar>
                            </Badge>
                        </Grid>
                        <Grid item xs>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="h4" fontWeight={700}>
                                    {profile.firstName} {profile.lastName}
                                </Typography>
                                <Verified sx={{ color: 'primary.main' }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {profile.title}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                <Chip icon={<Business />} label={profile.company} size="small" />
                                <Chip icon={<LocationOn />} label={profile.location} size="small" />
                                <Chip icon={<WorkOutline />} label={`${profile.yearsExperience} years experience`} size="small" />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Rating value={profile.stats.clientSatisfaction} precision={0.1} readOnly size="small" />
                                    <Typography variant="body2">({profile.stats.clientSatisfaction})</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {profile.stats.completedProjects} projects completed
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item>
                            <Button 
                                variant="contained" 
                                startIcon={<Edit />}
                                onClick={() => setEditMode(true)}
                            >
                                Edit Profile
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Assignment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700}>{profile.stats.activeProjects}</Typography>
                        <Typography variant="body2" color="text.secondary">Active Projects</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700}>{profile.stats.completedProjects}</Typography>
                        <Typography variant="body2" color="text.secondary">Completed</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <AttachMoney sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700}>$4.5M</Typography>
                        <Typography variant="body2" color="text.secondary">Budget Managed</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <TrendingUp sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700}>{profile.stats.onTimeDelivery}%</Typography>
                        <Typography variant="body2" color="text.secondary">On-Time Delivery</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Star sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700}>{profile.stats.clientSatisfaction}</Typography>
                        <Typography variant="body2" color="text.secondary">Client Rating</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Schedule sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight={700}>{profile.stats.avgProjectDuration}</Typography>
                        <Typography variant="body2" color="text.secondary">Avg Months/Project</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Bio and Specializations */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600}>About Me</Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {profile.bio}
                        </Typography>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" gutterBottom fontWeight={600}>Specializations</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {profile.specializations.map((spec, index) => (
                                <Chip key={index} label={spec} color="primary" variant="outlined" />
                            ))}
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" gutterBottom fontWeight={600}>Service Areas</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {profile.preferredLocations.map((loc, index) => (
                                <Chip key={index} icon={<LocationOn />} label={loc} />
                            ))}
                        </Box>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600}>Contact Information</Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><Email /></ListItemIcon>
                                <ListItemText primary={profile.email} secondary="Email" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><Phone /></ListItemIcon>
                                <ListItemText primary={profile.phone} secondary="Phone" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><LocationOn /></ListItemIcon>
                                <ListItemText primary={profile.location} secondary="Location" />
                            </ListItem>
                        </List>
                    </Paper>
                    
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600}>Certifications</Typography>
                        <List dense>
                            {profile.certifications.map((cert, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon><Verified color="success" /></ListItemIcon>
                                    <ListItemText primary={cert} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    // Portfolio Tab
    const PortfolioTab = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Project Portfolio</Typography>
                <Button variant="contained" startIcon={<Add />}>Add Project</Button>
            </Box>

            <Grid container spacing={3}>
                {portfolio.map((project) => (
                    <Grid item xs={12} md={4} key={project.id}>
                        <Card>
                            <Box sx={{ 
                                height: 160, 
                                bgcolor: 'grey.200', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                            }}>
                                <Business sx={{ fontSize: 60, color: 'grey.400' }} />
                            </Box>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>{project.name}</Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Chip label={project.type} size="small" />
                                    <Chip label={project.year} size="small" variant="outlined" />
                                </Box>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Value</Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            ${project.value.toLocaleString()}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Duration</Typography>
                                        <Typography variant="body2" fontWeight={600}>{project.duration}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    // Reviews Tab
    const ReviewsTab = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" fontWeight={600}>Client Reviews</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Rating value={profile.stats.clientSatisfaction} precision={0.1} readOnly />
                        <Typography variant="body1" fontWeight={600}>{profile.stats.clientSatisfaction}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            ({reviews.length} reviews)
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {reviews.map((review) => (
                <Paper key={review.id} sx={{ p: 3, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar>{review.client[0]}</Avatar>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600}>{review.client}</Typography>
                                <Typography variant="body2" color="text.secondary">{review.project}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Rating value={review.rating} readOnly size="small" />
                            <Typography variant="caption" display="block" color="text.secondary">
                                {new Date(review.date).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                        "{review.comment}"
                    </Typography>
                </Paper>
            ))}
        </Box>
    );

    // Settings Tab
    const SettingsTab = () => (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight={600}>Account Settings</Typography>
            
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                            <Notifications sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Notification Preferences
                        </Typography>
                        <FormControlLabel
                            control={<Switch checked={profile.emailNotifications} />}
                            label="Email Notifications"
                        />
                        <FormControlLabel
                            control={<Switch checked={profile.whatsappNotifications} />}
                            label="WhatsApp Notifications"
                        />
                        <FormControlLabel
                            control={<Switch checked={profile.smsNotifications} />}
                            label="SMS Notifications"
                        />
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                            <MyLocation sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Geofencing Settings
                        </Typography>
                        <FormControlLabel
                            control={<Switch defaultChecked />}
                            label="Enable Location Services"
                        />
                        <FormControlLabel
                            control={<Switch defaultChecked />}
                            label="Auto Check-in at Sites"
                        />
                        <FormControlLabel
                            control={<Switch />}
                            label="Share Location with Clients"
                        />
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                            <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Availability
                        </Typography>
                        <FormControlLabel
                            control={<Switch checked={profile.availableForRemote} />}
                            label="Available for Remote Project Management"
                        />
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Project Value Range
                            </Typography>
                            <Typography variant="body1">
                                ${profile.minProjectValue.toLocaleString()} - ${profile.maxProjectValue.toLocaleString()}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                            <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Security
                        </Typography>
                        <Button variant="outlined" fullWidth sx={{ mb: 2 }}>
                            Change Password
                        </Button>
                        <Button variant="outlined" fullWidth sx={{ mb: 2 }}>
                            Two-Factor Authentication
                        </Button>
                        <Button variant="outlined" fullWidth color="error">
                            Download My Data
                        </Button>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab icon={<Person />} label="Overview" iconPosition="start" />
                    <Tab icon={<Assignment />} label="Portfolio" iconPosition="start" />
                    <Tab icon={<Star />} label="Reviews" iconPosition="start" />
                    <Tab icon={<Settings />} label="Settings" iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            <TabPanel value={activeTab} index={0}><OverviewTab /></TabPanel>
            <TabPanel value={activeTab} index={1}><PortfolioTab /></TabPanel>
            <TabPanel value={activeTab} index={2}><ReviewsTab /></TabPanel>
            <TabPanel value={activeTab} index={3}><SettingsTab /></TabPanel>

            {/* Edit Profile Dialog */}
            <Dialog open={editMode} onClose={() => setEditMode(false)} maxWidth="md" fullWidth>
                <DialogCloseButton onClick={() => setEditMode(false)} />
                <DialogTitle>Edit Profile</DialogTitle>
                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={profile.firstName}
                                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={profile.lastName}
                                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Title"
                                value={profile.title}
                                onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Company"
                                value={profile.company}
                                onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <PhoneField
                                label="Phone"
                                value={profile.phone}
                                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={profile.location}
                                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Bio"
                                value={profile.bio}
                                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Years of Experience"
                                value={profile.yearsExperience}
                                onChange={(e) => setProfile({ ...profile, yearsExperience: parseInt(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Preferred Contact Method</InputLabel>
                                <Select
                                    value={profile.preferredContactMethod}
                                    label="Preferred Contact Method"
                                    onChange={(e) => setProfile({ ...profile, preferredContactMethod: e.target.value })}
                                >
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="phone">Phone</MenuItem>
                                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                                    <MenuItem value="video">Video Call</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditMode(false)}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSaveProfile}
                        disabled={loading}
                        startIcon={loading ? null : <Save />}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ProjectManagerProfile;








